/**
 * Footage coverage — the ledger's reader.
 *
 * 🔴 Why this screen is the mission and not a report. Every other check in the
 * product inspects the EDGES of a recording: is the camera registered, is it
 * enabled, did it write recently. All three stay true across a day with a hole
 * in the middle of it, and the hole is the thing a customer asks about.
 *
 * ⚠️ Three refusals in db/coverage.ts are load-bearing and this screen must not
 * undo them by rendering around them:
 *
 *  - A day with NO ROW was never measured. It is not 0%, and it must not be
 *    drawn as an empty cell in a run of full ones — retention deletes the
 *    evidence a day is measured from, so "no row" mostly means "older than the
 *    footage", which is normal and is not a fault.
 *  - An `indeterminate` day could not be READ. It is counted apart and rendered
 *    apart, never averaged as though it were empty.
 *  - The ledger never downgrades itself, so a figure here only ever improves as
 *    better evidence arrives. A number that went down is a bug, not a fault.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, type CoverageDay, type CoverageResponse } from './api.ts';
import {
  Banner,
  DataTable,
  Empty,
  Loading,
  Pill,
  Section,
  Stat,
  StatRow,
  Toolbar,
  type ColumnDef,
  type PillState,
} from './components/index.ts';

const DAY_MS = 86_400_000;

function hours(ms: number): string {
  return `${(ms / 3_600_000).toFixed(1)}h`;
}

/** The status vocabulary, deliberately the product's own and not a new one. */
function verdictState(pct: number | null, verdict: string): PillState {
  if (verdict !== 'measured') return 'stale';
  if (pct === null) return 'stale';
  if (pct >= 99.5) return 'ok';
  if (pct > 0) return 'warning';
  return 'fault';
}

/*
 * The four counts are camera-days, not cameras, and `indeterminate` is kept
 * apart rather than averaged in — see `CoverageResponse.summary` in api.ts for
 * why every way of folding it into one percentage misstates something.
 */
const INDETERMINATE_REASON =
  'The day could not be read — an unparseable segment name. Counted apart rather than averaged in: as covered it would hide a camera nobody could read, as a gap it would invent an outage.';

const COLUMNS: ColumnDef<CoverageDay, unknown>[] = [
  {
    id: 'camera',
    header: 'Camera',
    accessorFn: (d) => d.asset_code ?? d.asset_name ?? '',
    cell: ({ row }) => (
      <>
        {row.original.asset_code ?? row.original.asset_name ?? '—'}
        <div className="cell-sub">{row.original.recorder_key}</div>
      </>
    ),
  },
  {
    id: 'day',
    header: 'Day',
    accessorKey: 'day',
    cell: ({ getValue }) => <span className="mono">{getValue() as string}</span>,
  },
  {
    id: 'coverage',
    header: 'Coverage',
    accessorFn: (d) => (d.verdict === 'measured' && d.pct !== null ? Number(d.pct) : -1),
    meta: { align: 'right' },
    cell: ({ row }) => {
      const d = row.original;
      const pct = d.pct === null ? null : Number(d.pct);
      const measured = d.verdict === 'measured' && pct !== null;
      return (
        <>
          <Pill state={verdictState(pct, d.verdict)} reason={measured ? null : INDETERMINATE_REASON}>
            {measured ? `${pct}%` : 'indeterminate'}
          </Pill>
          {!measured && <div className="cell-sub">{d.unparsed_count} unreadable name(s)</div>}
        </>
      );
    },
  },
  {
    id: 'held',
    header: 'Held',
    accessorFn: (d) => Number(d.covered_ms),
    meta: { align: 'right' },
    cell: ({ row }) => (
      <div className="mono">
        {hours(Number(row.original.covered_ms))}
        <div className="cell-sub">of {hours(Number(row.original.window_ms) || DAY_MS)}</div>
      </div>
    ),
  },
  {
    id: 'gaps',
    header: 'Gaps',
    accessorKey: 'gap_count',
    meta: { align: 'right' },
    cell: ({ getValue }) => <span className="mono">{getValue() as number}</span>,
  },
  {
    id: 'longest',
    header: 'Longest gap',
    accessorFn: (d) => Number(d.longest_gap_ms),
    meta: { align: 'right' },
    cell: ({ getValue }) => <span className="mono">{hours(getValue() as number)}</span>,
  },
];

export function Recordings(): React.JSX.Element {
  const [data, setData] = useState<CoverageResponse | null>(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.get<CoverageResponse>(`/api/coverage?days=${days}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not load coverage');
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => data?.days ?? [], [data]);

  if (error !== null) return <Banner tone="fault">{error}</Banner>;
  if (data === null) return <Loading rows={6} cols={6} label="Loading coverage" />;

  const { summary } = data;
  const measured = summary.full + summary.partial + summary.none;

  if (measured === 0 && summary.indeterminate === 0) {
    return (
      <Empty
        title="No footage has been measured yet"
        body={
          'The coverage ledger writes one row per camera per day, from the segments the ' +
          'recorder still holds. Nothing here means the hourly pass has not run against ' +
          'this database yet — not that no footage exists.'
        }
      />
    );
  }

  return (
    <div className="stack">
      <Toolbar>
        <label className="small muted" htmlFor="coverage-days">
          Window
        </label>
        <select id="coverage-days" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </Toolbar>

      <StatRow>
        <Stat n={summary.full} label="Fully covered" state="ok" />
        <Stat n={summary.partial} label="Partial" state="warning" />
        <Stat n={summary.none} label="No footage" state="fault" />
        <Stat n={summary.indeterminate} label="Indeterminate" state="stale" />
      </StatRow>

      <Section
        flush
        title="Camera-days"
        summary="Measured from the segments the recorder still holds, so it sees the middle of a day. A day with no row was never measured — retention, not a gap."
      >
        <DataTable
          data={rows}
          columns={COLUMNS}
          getRowId={(d) => `${d.asset_id}-${d.recorder_key}-${d.day}`}
          initialSort={[{ id: 'day', desc: true }]}
          caption="Footage coverage by camera and day"
          empty={<Empty title="No camera-days in this window" />}
        />
      </Section>
    </div>
  );
}
